/**
 * Modal URL Builder — URL construction helpers
 *
 * Constructs modal-aware URLs and parameter objects from a modal ID and
 * entity-specific params. Pairs with `modal-url-parse.ts` (extraction +
 * validation) and `modal-url-types.ts` (shared interfaces).
 */

import {
  getModalConfig,
  isModalConfigured,
  type ConfiguredModalId,
  type ModalParams,
} from "./modal-url-config";
import type { BuildModalUrlOptions } from "./modal-url-types";

/**
 * Build a complete URL for opening a modal with entity parameters
 *
 * This function constructs a properly formatted URL that includes:
 * - The modal ID in the 'modal' query parameter
 * - Entity ID in the appropriate parameter (singular or plural)
 * - Any additional parameters specified
 *
 * @param modalId - The modal ID to open
 * @param params - Entity ID and additional parameters
 * @param options - URL building options
 * @returns A complete URL string
 *
 * @example
 * ```tsx
 * // Basic usage
 * buildModalUrl('deals-detail', { dealsId: '123' });
 * // => "/current-path?modal=deals-detail&dealsId=123"
 *
 * // With custom pathname
 * buildModalUrl('contacts-detail', { contactsId: '456' }, {
 *   pathname: '/dashboard'
 * });
 * // => "/dashboard?modal=contacts-detail&contactsId=456"
 *
 * // Preserve existing params
 * buildModalUrl('tasks-detail', { tasksId: '789' }, {
 *   preserveParams: true,
 *   searchParams: new URLSearchParams('view=list')
 * });
 * // => "/current-path?view=list&modal=tasks-detail&tasksId=789"
 * ```
 */
export function buildModalUrl<T extends ConfiguredModalId>(
  modalId: T,
  params: ModalParams<T> & Record<string, string | undefined>,
  options: BuildModalUrlOptions = {}
): string {
  const {
    pathname = typeof window !== "undefined" ? window.location.pathname : "",
    searchParams = null,
    preserveParams = false,
    additionalParams = {},
  } = options;

  // Start with existing params if preserving, otherwise fresh
  const urlParams = preserveParams && searchParams
    ? new URLSearchParams(searchParams.toString())
    : new URLSearchParams();

  // Add modal parameter
  urlParams.set("modal", modalId);

  // Add entity ID if provided and modal is configured
  if (isModalConfigured(modalId)) {
    const config = getModalConfig(modalId);

    // Check for entity ID in both singular and plural forms
    const entityId = (params as Record<string, string | undefined>)[config.pluralParam]
      || (params as Record<string, string | undefined>)[config.singularParam];

    if (entityId) {
      // Always use plural form for consistency
      urlParams.set(config.pluralParam, entityId);
      // Remove singular form if it exists
      urlParams.delete(config.singularParam);
    }
  }

  // Add any additional parameters from the params object
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && key !== "modal") {
      const config = isModalConfigured(modalId) ? getModalConfig(modalId) : null;
      // Skip entity ID params (already handled above)
      if (config && (key === config.singularParam || key === config.pluralParam)) {
        return;
      }
      urlParams.set(key, value);
    }
  });

  // Add additional parameters from options
  Object.entries(additionalParams).forEach(([key, value]) => {
    if (value !== undefined) {
      urlParams.set(key, value);
    }
  });

  const queryString = urlParams.toString();
  return `${pathname}${queryString ? `?${queryString}` : ""}`;
}

/**
 * Build URL parameters object for a modal
 *
 * Creates a params object suitable for use with Next.js router
 *
 * @param modalId - Modal ID
 * @param entityId - Entity ID to include
 * @param additionalParams - Additional parameters
 * @returns Object with modal and entity parameters
 *
 * @example
 * ```tsx
 * const params = buildModalParams('deals-detail', '123', { tab: 'overview' });
 * router.push({ pathname: '/deals', query: params });
 * ```
 */
export function buildModalParams<T extends ConfiguredModalId>(
  modalId: T,
  entityId?: string,
  additionalParams?: Record<string, string>
): Record<string, string> {
  const params: Record<string, string> = {
    modal: modalId,
  };

  if (entityId && isModalConfigured(modalId)) {
    const config = getModalConfig(modalId);
    params[config.pluralParam] = entityId;
  }

  if (additionalParams) {
    Object.assign(params, additionalParams);
  }

  return params;
}

/**
 * Update modal URL parameters while preserving other params
 *
 * @param currentParams - Current URL search parameters
 * @param modalId - Modal ID to update
 * @param entityId - New entity ID (optional)
 * @param additionalParams - Additional parameters to update
 * @returns New URLSearchParams object
 */
export function updateModalParams<T extends ConfiguredModalId>(
  currentParams: URLSearchParams | null | undefined,
  modalId: T,
  entityId?: string,
  additionalParams?: Record<string, string | undefined>
): URLSearchParams {
  const newParams = new URLSearchParams(currentParams?.toString() || "");

  // Update modal ID
  newParams.set("modal", modalId);

  // Update entity ID if provided
  if (entityId && isModalConfigured(modalId)) {
    const config = getModalConfig(modalId);
    newParams.set(config.pluralParam, entityId);
    newParams.delete(config.singularParam);
  }

  // Update additional parameters
  if (additionalParams) {
    Object.entries(additionalParams).forEach(([key, value]) => {
      if (value !== undefined) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
  }

  return newParams;
}
